// Agenda des événements : vue mois + liste, filtres par catégorie, lien public partageable
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, isSameMonth, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, MapPin, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EventDialog } from "@/components/EventDialog";
import { CATEGORIES_AGENDA } from "@/lib/constants";
import { formatHeure } from "@/lib/dates";

const peutGerer = (u) => ["admin", "media", "ca"].includes(u?.role) || !!u?.pole_responsable;

const categoriesAutorisees = (u) => {
  if (["admin", "media", "ca"].includes(u?.role)) return Object.keys(CATEGORIES_AGENDA);
  return CATEGORIES_AGENDA[u?.pole_responsable] ? [u.pole_responsable] : [];
};

export default function Agenda() {
  const { user } = useAuth();
  const gestion = peutGerer(user);
  const [events, setEvents] = useState([]);
  const [dateCourante, setDateCourante] = useState(new Date());
  const [filtre, setFiltre] = useState("toutes");
  const [dialog, setDialog] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const visibles = filtre === "toutes" ? events : events.filter((e) => e.categorie === filtre);

  const jours = useMemo(() => {
    const debut = startOfWeek(startOfMonth(dateCourante), { weekStartsOn: 1 });
    const fin = endOfWeek(endOfMonth(dateCourante), { weekStartsOn: 1 });
    const liste = [];
    for (let d = debut; d <= fin; d = addDays(d, 1)) liste.push(d);
    return liste;
  }, [dateCourante]);

  const eventsDuJour = (jour) => visibles.filter((e) => e.date_debut?.slice(0, 10) === format(jour, "yyyy-MM-dd"));

  const Chip = ({ e }) => {
    const cat = CATEGORIES_AGENDA[e.categorie];
    return (
      <button
        onClick={() => gestion && setDialog({ event: e })}
        className="w-full text-left rounded-lg px-2 py-1.5 text-xs transition-transform hover:-translate-y-0.5"
        style={{ background: `${cat?.couleur}1f`, borderLeft: `3px solid ${cat?.couleur}` }}
        data-testid={`agenda-event-${e.id}`}
      >
        <span className="font-medium block truncate">
          {formatHeure(e.date_debut)} · {e.titre}
        </span>
        {e.public === false && (
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
            <EyeOff className="h-3 w-3" /> interne
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-bold">Agenda</h1>
          <p className="text-muted-foreground text-sm mt-1">Répétitions, prestations, cours et réunions de l'ASBL</p>
        </div>
        {gestion && (
          <Button onClick={() => setDialog({ defaultDate: format(new Date(), "yyyy-MM-dd") })} className="rounded-full gap-1.5" data-testid="new-event-button">
            <Plus className="h-4 w-4" /> Nouvel événement
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex items-center gap-1 rounded-full bg-card border border-border p-1">
          <button onClick={() => setDateCourante(addMonths(dateCourante, -1))} className="p-1.5 rounded-full hover:bg-muted transition-colors" data-testid="agenda-prev"><ChevronLeft className="h-4 w-4" /></button>
          <span className="px-2 text-sm font-semibold first-letter:capitalize min-w-[140px] text-center" data-testid="agenda-period-title">
            {format(dateCourante, "MMMM yyyy", { locale: fr })}
          </span>
          <button onClick={() => setDateCourante(addMonths(dateCourante, 1))} className="p-1.5 rounded-full hover:bg-muted transition-colors" data-testid="agenda-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <Button variant="outline" size="sm" onClick={() => setDateCourante(new Date())} className="rounded-full h-9">Aujourd'hui</Button>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setFiltre("toutes")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${filtre === "toutes" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}
            data-testid="agenda-filter-toutes"
          >
            Toutes
          </button>
          {Object.entries(CATEGORIES_AGENDA).map(([k, v]) => (
            <button
              key={k}
              onClick={() => setFiltre(k)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${filtre === k ? "text-white border-transparent" : "border-border text-muted-foreground hover:bg-muted"}`}
              style={filtre === k ? { background: v.couleur } : { borderLeft: `3px solid ${v.couleur}` }}
              data-testid={`agenda-filter-${k}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grille mois (desktop) */}
      <div className="hidden md:block bg-card rounded-2xl border border-border overflow-hidden carte-douce">
        <div className="grid grid-cols-7 border-b border-border">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((j) => (
            <div key={j} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">{j}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {jours.map((jour) => {
            const horsMois = !isSameMonth(jour, dateCourante);
            return (
              <div
                key={jour.toISOString()}
                className={`group border-b border-r border-border/60 p-1.5 space-y-1 min-h-[110px] ${horsMois ? "bg-muted/30" : ""}`}
                data-testid={`agenda-day-${format(jour, "yyyy-MM-dd")}`}
              >
                <div className="flex items-center justify-between px-1">
                  <span className={`text-xs font-semibold h-6 w-6 flex items-center justify-center rounded-full ${isToday(jour) ? "bg-primary text-primary-foreground" : horsMois ? "text-muted-foreground/50" : "text-muted-foreground"}`}>
                    {format(jour, "d")}
                  </span>
                  {gestion && (
                    <button
                      onClick={() => setDialog({ defaultDate: format(jour, "yyyy-MM-dd") })}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded-full hover:bg-muted transition-opacity"
                      data-testid={`agenda-day-add-${format(jour, "yyyy-MM-dd")}`}
                    >
                      <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
                {eventsDuJour(jour).map((e) => <Chip key={e.id} e={e} />)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Liste (mobile + prochains événements) */}
      <div className="space-y-3" data-testid="agenda-list">
        <h2 className="font-serif text-lg md:text-lg font-semibold">Prochains événements</h2>
        {visibles
          .filter((e) => e.date_debut >= format(new Date(), "yyyy-MM-dd"))
          .slice(0, 12)
          .map((e) => {
            const cat = CATEGORIES_AGENDA[e.categorie];
            return (
              <button
                key={e.id}
                onClick={() => gestion && setDialog({ event: e })}
                className="w-full text-left flex items-start gap-4 bg-card rounded-2xl border border-border px-5 py-4 carte-douce"
                data-testid={`agenda-list-item-${e.id}`}
              >
                <span className="h-10 w-1 rounded-full shrink-0" style={{ background: cat?.couleur }} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{e.titre}</p>
                    <Badge variant="outline" className="text-[10px]">{cat?.label}</Badge>
                    {e.public === false && <Badge className="text-[10px] bg-muted text-muted-foreground border-0">Interne</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5 first-letter:capitalize">
                    {format(new Date(e.date_debut), "EEEE d MMMM yyyy 'à' HH'h'mm", { locale: fr })}
                  </p>
                  {e.lieu && (
                    <p className="text-xs text-muted-foreground/80 mt-1 inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {e.lieu}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        {visibles.length === 0 && (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="agenda-empty">Aucun événement pour l'instant.</p>
        )}
      </div>

      <EventDialog
        open={Boolean(dialog)}
        onClose={() => setDialog(null)}
        event={dialog?.event}
        defaultDate={dialog?.defaultDate}
        categoriesAutorisees={categoriesAutorisees(user)}
        onSaved={charger}
      />
    </div>
  );
}
